import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosError, AxiosResponse } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Lanelets Center Line Points 生成リクエストAPI
 */
export const useCreateLaneletsCenterLinePointsAPI = (): {
  state: APIState;
  postLaneletsCenterLinePoints: (areaMapVersionId: string) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      areaMapId: number,
      areaMapVersionId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/area_maps/${areaMapId}/versions/${areaMapVersionId}/generate_lanelets_center_line_points`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postLaneletsCenterLinePoints = useAtomCallback(
    useCallback(
      async (get, _, areaMapVersionId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_generate_lanelets_center_line_points', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          environment.area_map_id,
          areaMapVersionId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_generate_lanelets_center_line_points', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_generate_lanelets_center_line_points', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    postLaneletsCenterLinePoints,
  };
};
